import { Fragment, useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Activity, AlertTriangle, ArrowLeft, Cpu, HardDrive, Loader2, MemoryStick,
  RefreshCw, Server, Shield, Timer,
} from "lucide-react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { fmtDate } from "../lib/support";

const UNSET = "UNDETERMINED";
const mb = (v) => (v == null ? UNSET : `${Number(v).toLocaleString("en-US", { maximumFractionDigits: 1 })} MB`);
const gb = (v) => (v == null ? UNSET : `${Number(v).toLocaleString("en-US", { maximumFractionDigits: 1 })} GB`);
const secs = (v) => (v == null ? "—" : `${Number(v).toLocaleString("en-US", { maximumFractionDigits: 1 })}s`);
const pct = (v) => (v == null ? "—" : `${v}%`);

const uptime = (s) => {
  if (s == null) return "—";
  const t = Math.round(s);
  if (t < 90) return `${t}s`;
  if (t < 5400) return `${Math.floor(t / 60)}m`;
  return `${Math.floor(t / 3600)}h ${Math.floor((t % 3600) / 60)}m`;
};

function Kpi({ label, value, sub, icon: Icon, tid, warn }) {
  return (
    <div
      className={`border p-5 ${warn ? "border-amber-500/40 bg-amber-500/[0.04]" : "border-zinc-800 bg-zinc-900/40"}`}
      data-testid={tid}
    >
      <div className="flex items-center gap-2 text-[10px] font-mono uppercase tracking-widest text-zinc-500 mb-3">
        {Icon ? <Icon className="w-3 h-3" /> : null} {label}
      </div>
      <div className={`font-heading text-2xl font-black tracking-tighter break-all ${warn ? "text-amber-300" : "text-white"}`}>
        {value}
      </div>
      <div className="text-[11px] font-mono text-zinc-500 mt-2">{sub || "\u00A0"}</div>
    </div>
  );
}

function DetailRow({ label, value, tid }) {
  return (
    <div className="flex items-baseline justify-between gap-4 py-2 border-b border-zinc-800/60 last:border-0">
      <span className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 shrink-0">{label}</span>
      <span className="text-xs font-mono text-zinc-300 text-right break-all" data-testid={tid}>{value}</span>
    </div>
  );
}

function StageTable({ job }) {
  const stages = job.stages || [];
  if (!stages.length) {
    return (
      <div className="text-xs font-mono text-zinc-500 px-4 py-3" data-testid={`diag-job-nostages-${job.job_id}`}>
        No stage rows recorded for this job.
      </div>
    );
  }
  return (
    <div className="px-4 py-4 space-y-4" data-testid={`diag-job-stages-${job.job_id}`}>
      <table className="w-full text-xs">
        <thead>
          <tr className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 text-left">
            <th className="pb-2 font-normal">Stage</th>
            <th className="pb-2 font-normal">Seconds</th>
            <th className="pb-2 font-normal">Peak RSS</th>
            <th className="pb-2 font-normal">Peak scratch</th>
            <th className="pb-2 font-normal">Detail</th>
          </tr>
        </thead>
        <tbody className="font-mono text-zinc-300">
          {stages.map((s, i) => {
            const extras = Object.entries(s).filter(
              ([k]) => !["stage", "ok", "seconds", "peak_rss_mb", "peak_scratch_mb", "error"].includes(k),
            );
            return (
              <tr key={`${s.stage}-${i}`} className="border-t border-zinc-800/60">
                <td className="py-2 pr-3">
                  <span className={s.ok === false ? "text-red-300" : "text-white"}>{s.stage}</span>
                  {s.ok === false ? <span className="ml-2 text-[10px] uppercase text-red-400">failed</span> : null}
                </td>
                <td className="py-2 pr-3">{secs(s.seconds)}</td>
                <td className="py-2 pr-3">{mb(s.peak_rss_mb)}</td>
                <td className="py-2 pr-3">{s.peak_scratch_mb == null ? "—" : mb(s.peak_scratch_mb)}</td>
                <td className="py-2 text-zinc-500">
                  {s.error ? <span className="text-red-300">{s.error}</span> : null}
                  {extras.map(([k, v]) => `${k}=${typeof v === "object" ? JSON.stringify(v) : v}`).join(" · ") || (s.error ? "" : "—")}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      {Object.keys(job.notes || {}).length ? (
        <div className="text-[11px] font-mono text-zinc-500 break-all">
          notes: {JSON.stringify(job.notes)}
        </div>
      ) : null}
    </div>
  );
}

export default function AdminDiagnosticsPage() {
  const navigate = useNavigate();
  const [env, setEnv] = useState(null);
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(null);

  const load = useCallback(async () => {
    setBusy(true);
    try {
      const [e, j] = await Promise.all([
        api.get("/admin/diagnostics/env"),
        api.get("/admin/diagnostics/jobs?limit=50"),
      ]);
      setEnv(e.data);
      setJobs(j.data.jobs || []);
    } catch (err) {
      if (err?.response?.status === 403) {
        toast.error("Admins only");
        navigate("/dashboard");
      } else {
        toast.error(err?.response?.data?.detail || "Could not load diagnostics");
      }
    } finally {
      setBusy(false);
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    load();
  }, [load]);

  if (loading) {
    return (
      <div className="grain-bg min-h-screen bg-zinc-950 text-white grid place-items-center">
        <Loader2 className="w-5 h-5 animate-spin text-zinc-500" />
      </div>
    );
  }

  const m = env?.memory || {};
  const c = env?.cpu || {};
  const d = env?.disk || {};
  const p = env?.process || {};
  const leftovers = d.workdir_leftovers || {};
  const limitUnknown = m.cgroup_limit_mb == null;

  return (
    <div className="grain-bg min-h-screen bg-zinc-950 text-white" data-testid="admin-diagnostics-page">
      <header className="border-b border-zinc-800 bg-zinc-950/80 backdrop-blur sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-4 md:px-8 h-16 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3 min-w-0">
            <Shield className="w-4 h-4 text-emerald-400 shrink-0" />
            <div className="text-[10px] font-mono uppercase tracking-widest text-emerald-400">Admin</div>
            <div className="font-heading text-lg font-bold tracking-tight truncate">Diagnostics</div>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={load}
              disabled={busy}
              data-testid="diag-refresh-btn"
              className="border border-zinc-800 hover:bg-zinc-800 px-3 py-1.5 text-[10px] uppercase font-mono tracking-widest inline-flex items-center gap-1.5 disabled:opacity-40"
            >
              {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />} Refresh
            </button>
            <Link
              to="/admin"
              data-testid="diag-back-to-admin"
              className="border border-zinc-800 hover:bg-zinc-800 px-3 py-1.5 text-[10px] uppercase font-mono tracking-widest inline-flex items-center gap-1.5"
            >
              <ArrowLeft className="w-3 h-3" /> Admin
            </Link>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 md:px-8 py-8 space-y-8">
        <section className="border border-blue-500/25 bg-blue-500/[0.04] p-4 space-y-1.5" data-testid="diag-scope-note">
          <div className="text-[10px] font-mono uppercase tracking-widest text-blue-300">
            Read-only · this container reports its own limits
          </div>
          <p className="text-xs text-zinc-400 leading-relaxed">
            These numbers describe the machine you are reading them on. Open this page on the
            <span className="text-zinc-200"> deployed </span> app to get real production limits —
            preview-pod figures are larger and must not be used for capacity planning.
          </p>
          <p className="text-[11px] font-mono text-zinc-500 pt-1">
            Captured {fmtDate(env?.captured_at)} · stale-job sweeper{" "}
            {env?.job_capacity_hint?.stale_job_minutes ?? 180} min · sampling every 2s per job
          </p>
        </section>

        <section className="grid grid-cols-2 lg:grid-cols-3 gap-4" data-testid="diag-env-cards">
          <Kpi
            label="Memory limit"
            value={mb(m.cgroup_limit_mb)}
            sub={limitUnknown ? "cgroup reports no limit" : `headroom ${mb(m.headroom_mb)}`}
            icon={MemoryStick}
            tid="diag-kpi-memory-limit"
            warn={limitUnknown}
          />
          <Kpi
            label="Process RSS"
            value={mb(m.process_rss_mb)}
            sub={`high water ${mb(m.process_rss_high_water_mb)}`}
            icon={Activity}
            tid="diag-kpi-rss"
          />
          <Kpi
            label="Container memory in use"
            value={mb(m.cgroup_current_mb)}
            sub={`peak ${mb(m.cgroup_peak_mb)}`}
            icon={Server}
            tid="diag-kpi-cgroup-current"
          />
          <Kpi
            label="CPU quota"
            value={c.cgroup_quota_cores == null ? UNSET : `${c.cgroup_quota_cores} cores`}
            sub={`${c.visible_cores ?? "—"} visible · load ${c.load_1m ?? "—"}`}
            icon={Cpu}
            tid="diag-kpi-cpu"
            warn={c.cgroup_quota_cores == null}
          />
          <Kpi
            label="Temp disk free"
            value={gb(d.tmp_free_gb)}
            sub={`${d.tmp_path || "/tmp"} · ${gb(d.tmp_total_gb)} total`}
            icon={HardDrive}
            tid="diag-kpi-tmp-free"
          />
          <Kpi
            label="Scratch in use"
            value={mb(d.scratch_mb)}
            sub={
              leftovers.count
                ? `${leftovers.count} orphaned workdir(s) · ${mb(leftovers.mb)}`
                : "0 orphaned workdirs"
            }
            icon={Timer}
            tid="diag-kpi-scratch"
            warn={Boolean(leftovers.count)}
          />
        </section>

        <section className="border border-zinc-800 bg-zinc-900/40 p-5" data-testid="diag-container-detail">
          <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 mb-3">Container</div>
          <div className="grid md:grid-cols-2 gap-x-10">
            <div>
              <DetailRow label="PID" value={p.pid ?? "—"} tid="diag-detail-pid" />
              <DetailRow label="Process uptime" value={uptime(p.uptime_seconds)} tid="diag-detail-uptime" />
              <DetailRow label="Python" value={p.python || "—"} tid="diag-detail-python" />
            </div>
            <div>
              <DetailRow label="FFmpeg" value={p.ffmpeg || UNSET} tid="diag-detail-ffmpeg" />
              <DetailRow label="App disk free" value={gb(d.app_free_gb)} tid="diag-detail-app-disk" />
              <DetailRow label="App disk total" value={gb(d.app_total_gb)} tid="diag-detail-app-total" />
            </div>
          </div>
        </section>

        <section className="space-y-3">
          <div className="flex items-center justify-between gap-4">
            <h2 className="text-base md:text-lg font-heading font-bold tracking-tight">Job stage diagnostics</h2>
            <span className="text-[11px] font-mono text-zinc-500">{jobs.length} job(s) with telemetry</span>
          </div>

          {!jobs.length ? (
            <div
              className="border border-zinc-800 bg-zinc-900/40 p-8 text-center space-y-2"
              data-testid="diag-jobs-empty"
            >
              <AlertTriangle className="w-4 h-4 text-zinc-600 mx-auto" />
              <p className="text-sm text-zinc-400">No job has recorded diagnostics yet.</p>
              <p className="text-xs font-mono text-zinc-600">
                Stage telemetry is written when a job finishes; jobs that ran before this feature have none.
              </p>
            </div>
          ) : (
            <div className="border border-zinc-800 overflow-x-auto" data-testid="diag-jobs-table">
              <table className="w-full text-xs min-w-[980px]">
                <thead className="bg-zinc-900/60">
                  <tr className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 text-left">
                    <th className="px-4 py-3 font-normal">Job</th>
                    <th className="px-4 py-3 font-normal">Status</th>
                    <th className="px-4 py-3 font-normal">Media</th>
                    <th className="px-4 py-3 font-normal">Wall clock</th>
                    <th className="px-4 py-3 font-normal">× realtime</th>
                    <th className="px-4 py-3 font-normal">Peak RSS</th>
                    <th className="px-4 py-3 font-normal">% of limit</th>
                    <th className="px-4 py-3 font-normal">Peak scratch</th>
                    <th className="px-4 py-3 font-normal">Resumes</th>
                    <th className="px-4 py-3 font-normal">Cleanup</th>
                  </tr>
                </thead>
                <tbody>
                  {jobs.map((j) => {
                    const isOpen = open === j.job_id;
                    return (
                      <Fragment key={j.job_id}>
                        <tr
                          onClick={() => setOpen(isOpen ? null : j.job_id)}
                          data-testid={`diag-job-row-${j.job_id}`}
                          className={`border-t border-zinc-800 cursor-pointer transition-colors ${
                            isOpen ? "bg-zinc-900/80" : "hover:bg-zinc-900/50"
                          }`}
                        >
                          <td className="px-4 py-3 font-mono text-white whitespace-nowrap">
                            {j.job_id}
                            <div className="text-[10px] text-zinc-600">{fmtDate(j.created_at)}</div>
                          </td>
                          <td className="px-4 py-3 font-mono">
                            <span className={j.status === "done" ? "text-emerald-300" : j.status === "error" || j.status === "timeout" ? "text-red-300" : "text-amber-300"}>
                              {j.status || "—"}
                            </span>
                            {j.failure_reason ? (
                              <div className="text-[10px] text-red-400/80 max-w-[160px] truncate">{j.failure_reason}</div>
                            ) : null}
                          </td>
                          <td className="px-4 py-3 font-mono text-zinc-300">{j.media_minutes || 0}m</td>
                          <td className="px-4 py-3 font-mono text-zinc-300">{secs(j.total_seconds)}</td>
                          <td className="px-4 py-3 font-mono text-zinc-300">{j.realtime_ratio == null ? "—" : `${j.realtime_ratio}×`}</td>
                          <td className="px-4 py-3 font-mono text-zinc-300">{mb(j.peak_process_rss_mb)}</td>
                          <td className="px-4 py-3 font-mono">
                            <span className={(j.peak_memory_pct_of_limit ?? 0) > 70 ? "text-red-300" : "text-zinc-300"}>
                              {pct(j.peak_memory_pct_of_limit)}
                            </span>
                          </td>
                          <td className="px-4 py-3 font-mono text-zinc-300">{mb(j.peak_scratch_mb)}</td>
                          <td className="px-4 py-3 font-mono">
                            <span className={j.resume_count ? "text-amber-300" : "text-zinc-500"}>{j.resume_count || 0}</span>
                          </td>
                          <td className="px-4 py-3 font-mono">
                            {j.cleanup_ok == null ? (
                              <span className="text-zinc-500">—</span>
                            ) : j.cleanup_ok ? (
                              <span className="text-emerald-300">ok</span>
                            ) : (
                              <span className="text-amber-300">leftovers</span>
                            )}
                          </td>
                        </tr>
                        {isOpen ? (
                          <tr key={`${j.job_id}-stages`} className="border-t border-zinc-800 bg-zinc-950/60">
                            <td colSpan={10} className="p-0">
                              <StageTable job={j} />
                            </td>
                          </tr>
                        ) : null}
                      </Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </section>
      </main>
    </div>
  );
}
